import { createAction, createReducer } from '@infl-ts/util-redux-act';
import { identity, pipe } from 'fp-ts/function';
import * as Set from 'fp-ts/Set';
import * as Str from 'fp-ts/string';
import { nanoid } from 'nanoid';
import { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
var freezeAction = createAction()('freeze');
var unfreezeAction = createAction()('unfreeze');
var strSetInsert = Set.insert(Str.Eq);
var strSetRemove = Set.remove(Str.Eq);
var defaultFreezeState = {
  ids: identity(Set.empty),
  frozen: false
};
var freezeReducer = createReducer(defaultFreezeState).on(freezeAction, function (_ref, id) {
  var ids = _ref.ids;
  return {
    ids: strSetInsert(id)(ids),
    frozen: true
  };
}).on(unfreezeAction, function (_ref2, id) {
  var ids = _ref2.ids;
  return pipe(ids, strSetRemove(id), function (ids) {
    return {
      ids: ids,
      frozen: !Set.isEmpty(ids)
    };
  });
});
var FreezerContext = /*#__PURE__*/createContext(null);
export var Freezer = function Freezer(_ref3) {
  var onFreeze = _ref3.onFreeze,
      onUnfreeze = _ref3.onUnfreeze,
      children = _ref3.children;
  var firstRender = useRef(true);

  var _useReducer = useReducer(freezeReducer, defaultFreezeState),
      frozen = _useReducer[0].frozen,
      dispatcher = _useReducer[1];

  useEffect(function () {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    frozen ? onFreeze() : onUnfreeze();
  }, [firstRender, frozen, onFreeze, onUnfreeze]);
  return /*#__PURE__*/_jsx(FreezerContext.Provider, {
    value: dispatcher,
    "data-element": "unknown",
    "data-component": "Freezer",
    "data-source-file": "freezer.tsx",
    children: children
  });
};
export var useFreezer = function useFreezer() {
  var _useState = useState(function () {
    return nanoid();
  }),
      freezeId = _useState[0];

  var dispatch = useContext(FreezerContext);
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  // These lines have eslint disabled because this hook depends on the Freezer
  // component to exist but cant internally enforce `dispatch` to be non null so
  // instead we have an error check for it

  var freeze = useCallback(function () {
    return dispatch(freezeAction(freezeId));
  }, [dispatch, freezeId]);
  var unfreeze = useCallback(function () {
    return dispatch(unfreezeAction(freezeId));
  }, [dispatch, freezeId]);
  /* eslint-enable @typescript-eslint/no-non-null-assertion */

  if (dispatch === null) throw new Error('useFreezer hook is missing Freezer Component');
  return {
    freeze: freeze,
    unfreeze: unfreeze
  };
};